import React, { useState, useEffect, useContext, useMemo } from 'react';

// CONTEXT
import { ToastContext } from 'context/ToastContext';
import { useSeasonTheme } from 'context/SeasonThemeContext';

// SERVICES
import { getThemeSite, updateThemeSite, getCustomButton, updateCustomButton } from 'services/settingService';

// COMPONENTS
import Loader from 'components/loaderComponent';
import Planner from 'components/administration/plannerComponent';
import ToggleSwitch from 'components/buttons/ToggleSwitch';
import * as LucideIcons from 'lucide-react';

const Settings = () => {
    const { addToast } = useContext(ToastContext);
    const { updateSeasonTheme } = useSeasonTheme();
    const [theme, setTheme] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [isSavingTheme, setIsSavingTheme] = useState(false);
    const [isSavingCustom, setIsSavingCustom] = useState(false);
    const [customButtonEnabled, setCustomButtonEnabled] = useState(false);
    const [customButtonUrl, setCustomButtonUrl] = useState('');
    const [customButtonIcon, setCustomButtonIcon] = useState('HelpCircle');
    const [customButtonTooltip, setCustomButtonTooltip] = useState('');
    const [showIconSuggestions, setShowIconSuggestions] = useState(false);

    useEffect(() => {
        const fetchAll = async () => {
            setIsLoading(true);
            try {
                const response = await getThemeSite();
                // Attendu: { hub_status: 'defaut' | 'noel' | 'halloween' }
                if (response?.data?.hub_status) {
                    setTheme(response.data.hub_status);
                } else if (response?.data?.theme) {
                    // fallback éventuel
                    setTheme(response.data.theme);
                } else {
                    setTheme('defaut');
                }
            } catch (error) {
                console.error('Erreur lors de la récupération du thème:', error);
                setTheme('defaut');
            }
            try {
                const response = await getCustomButton();
                setCustomButtonEnabled(!!response.enabled);
                setCustomButtonUrl(response.url || '');
                setCustomButtonIcon(response.icon || 'HelpCircle');
                setCustomButtonTooltip(response.tooltip || '');
            } catch (error) {
                console.error('Erreur lors de la récupération des paramètres du bouton personnalisé :', error);
                setCustomButtonEnabled(false);
                setCustomButtonUrl('');
                setCustomButtonIcon('HelpCircle');
                setCustomButtonTooltip('');
            }
            // Fin des chargements initiaux
            setIsLoading(false);
            }
            fetchAll();
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSavingTheme(true);
        try {
            const result = await updateThemeSite(theme);
            if (result?.status === 200) {
                // Mettre à jour le contexte saisonnier localement
                updateSeasonTheme(theme);
                addToast('Succès', 'Thème du site mis à jour.', null, null, 'success');
            } else {
                addToast('Erreur', 'La mise à jour du thème a échoué.', null, null, 'error');
            }
        } catch (error) {
            console.error('Erreur updateThemeSite:', error);
            addToast('Erreur', 'La mise à jour du thème a échoué.', null, null, 'error');
        } finally {
            setIsSavingTheme(false);
        }
    };

    const handleSubmitCustom = async (e) => {
        e.preventDefault();
        setIsSavingCustom(true);
        try {
            const result = await updateCustomButton(customButtonEnabled, customButtonUrl, customButtonIcon, customButtonTooltip);
            if (result?.status === 200) {
                addToast('Succès', 'Paramètres du bouton personnalisé mis à jour.', null, null, 'success');
            } else {
                addToast('Erreur', 'La mise à jour des paramètres du bouton personnalisé a échoué.', null, null, 'error');
            }
        } catch (error) {
            console.error('Erreur updateCustomButtonSettings:', error);
            addToast('Erreur', 'La mise à jour des paramètres du bouton personnalisé a échoué.', null, null, 'error');
        } finally {
            setIsSavingCustom(false);
        }
    };

    const sanitizedIconName = (customButtonIcon || '').trim();
    const iconPreviewName = sanitizedIconName && LucideIcons[sanitizedIconName]
        ? sanitizedIconName
        : 'HelpCircle';
    const CustomButtonIconPreview = LucideIcons[iconPreviewName] || LucideIcons.HelpCircle;

    const allIconNames = useMemo(() => {
        return Object.keys(LucideIcons).filter((key) => {
            const exp = LucideIcons[key];
            // Garder uniquement les exports qui ressemblent à des composants d'icônes:
            // - Nom en PascalCase
            // - Soit une function component
            // - Soit un forwardRef (objet avec render/displayName)
            const isPascal = /^[A-Z]/.test(key);
            if (!isPascal) return false;
            const isFn = typeof exp === 'function';
            const isForwardRefLike = !!exp && typeof exp === 'object' && (typeof exp.render === 'function' || typeof exp.type === 'function' || typeof exp.displayName === 'string');
            return isFn || isForwardRefLike;
        }).sort();
    }, []);

    const filteredIconNames = useMemo(() => {
        const q = (customButtonIcon || '').trim().toLowerCase();
        if (!q || q.length < 3) return [];
        return allIconNames.filter((n) => n.toLowerCase().includes(q)).slice(0, 30);
    }, [customButtonIcon, allIconNames]);

    if (isLoading) {
        return <Loader />;
    }

    return (
        <div className="h-full flex flex-col">
            <div className="flex flex-col lg:flex-row gap-6 h-full">
                {/* Colonne unique avec carte paramètre thème */}
                <div className="flex flex-col gap-6 w-full lg:w-1/3">
                    <form onSubmit={handleSubmit} className="p-4 bg-blanc dark:bg-dark-secondary rounded-lg shadow-dropping-light dark:shadow-dropping-dark space-y-4">
                        <h3 className="text-xl font-semibold text-gray-800 dark:text-blanc">Thème du HUB AI</h3>
                        <div className="flex flex-col gap-2 ml-4">
                            <label htmlFor="theme" className="block text-md font-medium text-gray-700 dark:text-gray-300">Choisir un thème</label>
                            <select
                                id="theme"
                                value={theme}
                                onChange={(e) => setTheme(e.target.value)}
                                className="w-full px-3 py-2 rounded-md border border-gray-300 dark:bg-dark-secondary dark:text-blanc focus:outline-none cursor-pointer"
                            >
                                <option value="default">Défaut</option>
                                <option value="christmas">Noël</option>
                                <option value="halloween">Halloween</option>
                                <option value="easter" disabled>Pâques</option>
                                <option value="new-year" disabled>Nouvel An</option>
                                <option value="april-fools" disabled>Poisson d'avril</option>
                            </select>
                        </div>

                        <div className="flex justify-end">
                            <button
                                type="submit"
                                className="px-4 py-2 bg-orange text-blanc font-semibold rounded-md shadow hover:bg-framboise focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition duration-150 ease-in-out"
                                disabled={isSavingTheme}
                            >
                                {isSavingTheme ? 'Enregistrement…' : 'Enregistrer'}
                            </button>
                        </div>
                    </form>

                    {/* Carte paramètre bouton personnalisé temporaire */}
                    <form onSubmit={handleSubmitCustom} className="p-4 bg-blanc dark:bg-dark-secondary rounded-lg shadow-dropping-light dark:shadow-dropping-dark space-y-4">
                        <h3 className="text-xl font-semibold text-gray-800 dark:text-blanc">Bouton personnalisé</h3>
                        <div className="flex items-center justify-between ml-1">
                            <span className="text-md font-medium text-gray-700 dark:text-gray-300">Afficher le bouton</span>
                            <ToggleSwitch
                                isActive={customButtonEnabled}
                                onChange={() => setCustomButtonEnabled(v => !v)}
                            />
                        </div>
                        <div className="flex flex-col gap-2 ml-1">
                            <label htmlFor="customIcon" className="block text-md font-medium text-gray-700 dark:text-gray-300">
                                Icône
                                <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">
                                    <a href="https://lucide.dev/icons" target="_blank" rel="noreferrer" className="underline hover:text-bleu dark:hover:text-emeraude">
                                        Voir la galerie d’icônes
                                    </a>
                                </span>
                            </label>
                            <div className="flex items-center gap-3 relative">
                                <input
                                    id="customIcon"
                                    type="text"
                                    placeholder="HelpCircle, Megaphone, MessageSquare..."
                                    value={customButtonIcon}
                                    onChange={(e) => setCustomButtonIcon(e.target.value)}
                                    onFocus={() => setShowIconSuggestions(true)}
                                    onBlur={() => setTimeout(() => setShowIconSuggestions(false), 120)}
                                    className="w-full px-3 py-2 rounded-md border border-gray-300 dark:bg-dark-secondary dark:text-blanc focus:outline-none"
                                    autoComplete="off"
                                />
                                <div className="flex items-center justify-center w-10 h-10 rounded-md border border-gray-300 dark:border-gray-600 bg-gray-100 dark:bg-dark">
                                    <CustomButtonIconPreview size={20} />
                                </div>
                                {showIconSuggestions && (
                                    <ul className="absolute left-0 right-0 top-full mt-1 max-h-72 overflow-auto rounded-md border border-gray-300 dark:border-gray-700 bg-blanc dark:bg-dark-secondary shadow-lg z-50">
                                        {filteredIconNames.length === 0 ? (
                                            <li className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400 select-none">
                                                {((customButtonIcon || '').trim().length < 3)
                                                    ? 'Tapez au moins 3 caractères pour rechercher…'
                                                    : 'Aucune icône trouvée'}
                                            </li>
                                        ) : (
                                            filteredIconNames.map((name) => {
                                                const IconComp = LucideIcons[name] || LucideIcons.HelpCircle;
                                                const isActive = name === iconPreviewName;
                                                return (
                                                    <li
                                                        key={name}
                                                        className={`px-3 py-2 text-sm cursor-pointer flex items-center gap-2 hover:bg-gray-100 dark:hover:bg-dark ${isActive ? 'bg-gray-50 dark:bg-dark/60' : ''}`}
                                                        onMouseDown={(e) => {
                                                            e.preventDefault();
                                                            setCustomButtonIcon(name);
                                                            setShowIconSuggestions(false);
                                                        }}
                                                    >
                                                        <IconComp size={16} />
                                                        <span className="text-gray-800 dark:text-gray-200">{name}</span>
                                                    </li>
                                                );
                                            })
                                        )}
                                    </ul>
                                )}
                            </div>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                                Saisir le nom exact d’une icône Lucide (respecte la casse).
                            </p>
                        </div>
                        <div className="flex flex-col gap-2 ml-1">
                            <label htmlFor="customTooltip" className="block text-md font-medium text-gray-700 dark:text-gray-300">Description</label>
                            <input
                                id="customTooltip"
                                type="text"
                                placeholder="Description du bouton"
                                value={customButtonTooltip}
                                onChange={(e) => setCustomButtonTooltip(e.target.value)}
                                className="w-full px-3 py-2 rounded-md border border-gray-300 dark:bg-dark-secondary dark:text-blanc focus:outline-none"
                            />
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                                Ex: Posez vos questions sur le point d'actualité
                            </p>
                        </div>
                        <div className="flex flex-col gap-2 ml-1">
                            <label htmlFor="customUrl" className="block text-md font-medium text-gray-700 dark:text-gray-300">URL</label>
                            <input
                                id="customUrl"
                                type="url"
                                placeholder="https://…"
                                value={customButtonUrl}
                                onChange={(e) => setCustomButtonUrl(e.target.value)}
                                className="w-full px-3 py-2 rounded-md border border-gray-300 dark:bg-dark-secondary dark:text-blanc focus:outline-none"
                            />
                        </div>
                        <div className="flex justify-end">
                            <button
                                type="submit"
                                className="px-4 py-2 bg-orange text-blanc font-semibold rounded-md shadow hover:bg-framboise focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition duration-150 ease-in-out"
                                disabled={isSavingCustom}
                            >
                                {isSavingCustom ? 'Enregistrement…' : 'Enregistrer'}
                            </button>
                        </div>
                    </form>
                </div>
                {/* Colonne de droite avec le planificateur de thèmes*/}
                <div className="flex flex-col gap-6 w-full lg:w-2/3">
                    <div className="p-4 bg-blanc dark:bg-dark-secondary rounded-lg shadow-dropping-light dark:shadow-dropping-dark space-y-4">
                        <Planner />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Settings;